import { useMemo } from "react";

import { LoadingRedoUndoOutlined, MagicFilled } from "@aircall/icons";
import {
  Box,
  Flex,
  Icon,
  Textarea,
  Toggle,
  Typography,
  noop,
} from "@aircall/tractor";
import { Maybe } from "graphql/jsutils/Maybe";
import { useTranslation } from "react-i18next";

import { pickIcon } from "./EvaluationField.utilities";

import { InputChoices, InputChoicesProps } from "@/components/InputChoices";
import {
  InputChoice,
  InputChoiceProps,
} from "@/components/InputChoices/components/InputChoice";
import { InputChoiceIcon } from "@/components/InputChoices/components/InputChoiceIcon";
import { InputChoiceLabel } from "@/components/InputChoices/components/InputChoiceLabel";
import { Slider, SliderProps } from "@/components/Slider";
import { BinaryAnswerValue } from "@/graphql/generated/graphql";

import { EvaluationFieldProps } from ".";
import { ExcludeNotApplicable } from "../EvaluationResult/EvaluationResult.decl";

export function EvaluationTextarea<Value extends string>({
  comment,
  loading,
  hasAiAnswer,
  onChangeComment,
}: Pick<
  EvaluationFieldProps<Value>,
  "comment" | "loading" | "hasAiAnswer" | "onChangeComment"
>) {
  const { t } = useTranslation();

  return (
    <Flex flexDirection="column" gap="xxs">
      <Textarea
        value={comment}
        onChange={onChangeComment}
        placeholder={loading ? undefined : t("evaluation.add_feedback")}
        disabled={loading}
        style={{
          cursor: loading ? "not-allowed" : "auto",
        }}
      />
      {loading && (
        <Flex alignItems="center" gap="xxxs">
          <Icon spin component={LoadingRedoUndoOutlined} />
          <Typography variant="supportingRegularS">
            {t("evaluation.checking_ai_answers")}
          </Typography>
        </Flex>
      )}
      {!loading && hasAiAnswer && (
        <Flex alignItems="center" gap="xxxs">
          <Typography
            variant="supportingMediumS"
            color="text-secondary"
            data-test="generated-by-ai"
          >
            {t("evaluation.has_ai_answer")}
          </Typography>
          <MagicFilled size={16} color="icon-default" />
        </Flex>
      )}
    </Flex>
  );
}

function InputSlider<Value extends string>({
  onChange,
  value,
  ratingScale,
  maximumPoints,
  disabled,
}: Pick<EvaluationFieldProps<Value>, "ratingScale" | "maximumPoints"> & {
  onChange: EvaluationFieldProps<Value>["onChangeRating"];
  value: Maybe<Value>;
  disabled?: boolean;
}) {
  const { t } = useTranslation();

  const ratingScaleWithoutNotApplicable = useMemo(() => {
    const { NOT_APPLICABLE: _, ...rest } = ratingScale as {
      NOT_APPLICABLE: number;
    } & Record<Value, number>;

    return rest;
  }, [ratingScale]);

  const marks: SliderProps["marks"] = useMemo(
    () =>
      Object.entries<number>(ratingScaleWithoutNotApplicable).map(
        ([key, value]) => ({
          value,
          label: t(key.toLowerCase()),
        })
      ),
    [ratingScaleWithoutNotApplicable, t]
  );

  const sliderValue = useMemo(
    () =>
      value
        ? ratingScaleWithoutNotApplicable[
            value as keyof typeof ratingScaleWithoutNotApplicable
          ]
        : 0,
    [ratingScaleWithoutNotApplicable, value]
  );
  const sliderSteps = useMemo(
    () =>
      maximumPoints / (Object.keys(ratingScaleWithoutNotApplicable).length - 1),
    [maximumPoints, ratingScaleWithoutNotApplicable]
  );
  const entries = useMemo(
    () => Object.entries<number>(ratingScaleWithoutNotApplicable),
    [ratingScaleWithoutNotApplicable]
  );

  const onChangeSlider = (value: number) => {
    const found = entries.find(([, scaleValue]) => scaleValue === value);

    if (!found) {
      return null;
    }

    const rating = found.at(0) as Value;

    onChange?.(rating);
  };

  return (
    <Box m="0 m">
      <Slider
        onMouseDown={() => {
          if (value === null) {
            onChangeSlider(sliderValue);
          }
        }}
        value={sliderValue}
        min={0}
        max={maximumPoints}
        onChange={onChangeSlider}
        step={sliderSteps}
        marks={marks}
        disabled={disabled}
        hideThumb={value === null}
      />
    </Box>
  );
}

function TotalPoints<Value extends string>({
  ratingScale,
  maximumPoints,
  notApplicable,
  rating,
}: Pick<
  EvaluationFieldProps<Value>,
  "maximumPoints" | "notApplicable" | "ratingScale" | "rating"
>) {
  const { t } = useTranslation();

  if (notApplicable) {
    return (
      <Typography variant="supportingRegularS" color="text-disabled">
        {t("evaluation.question_summary_points", {
          text: "--",
        })}
      </Typography>
    );
  }

  if (!rating) {
    return (
      <Typography variant="supportingRegularS" color="text-secondary">
        {t("evaluation.question_summary_points", {
          text: maximumPoints,
        })}
      </Typography>
    );
  }

  return (
    <Typography variant="supportingRegularS" color="text-secondary">
      {t("evaluation.question_summary_points", {
        text: `${ratingScale[rating]} / ${maximumPoints}`,
      })}
    </Typography>
  );
}

export function PickInputChoices<Value extends string>({
  mode,
  value,
  disabled,
  ratingScale,
  maximumPoints,
  onChange,
}: Pick<InputChoicesProps<Value>, "mode" | "value" | "onChange"> &
  Pick<InputChoiceProps<Value>, "disabled"> &
  Pick<EvaluationFieldProps<Value>, "ratingScale" | "maximumPoints">) {
  const { t } = useTranslation();

  if (mode === "binary") {
    return (
      <InputChoices mode={mode} value={value} onChange={onChange}>
        {Object.keys(ratingScale)
          .filter((value) => value !== "NOT_APPLICABLE")
          .map((value) => {
            const Icon = pickIcon(
              value as ExcludeNotApplicable<BinaryAnswerValue>
            );

            return (
              <InputChoice key={value} disabled={disabled} value={value}>
                {Icon !== null && (
                  <InputChoiceIcon>
                    <Icon size={16} />
                  </InputChoiceIcon>
                )}
                <InputChoiceLabel>{t(value.toLowerCase())}</InputChoiceLabel>
              </InputChoice>
            );
          })}
      </InputChoices>
    );
  }

  if (mode === "multiple") {
    return (
      <InputSlider
        onChange={onChange}
        maximumPoints={maximumPoints}
        value={value}
        ratingScale={ratingScale}
        disabled={disabled}
      />
    );
  }

  throw new Error("Invalid mode");
}
export function EvaluationField<Value extends string>({
  mode,
  rank,
  question,
  ratingScale,
  maximumPoints,
  rating,
  comment,
  description,
  notApplicable,
  possiblyNotApplicable,
  renderInputChoices,
  renderNotApplicableToggle,
  renderFeedback,
  loading = false,
  hasAiAnswer = false,
  onChangeRating = noop,
  onChangeComment = noop,
  onChangeNotApplicable = noop,
}: EvaluationFieldProps<Value>) {
  const { t } = useTranslation();

  const overflowedRating = useMemo(
    () =>
      Object.values<number>(ratingScale).find(
        (rating) => rating > maximumPoints
      ),
    [maximumPoints, ratingScale]
  );

  if (overflowedRating) {
    throw new Error(
      `Rating scale overflowed. Maximum points is ${maximumPoints} but rating scale has a value of ${overflowedRating}`
    );
  }

  return (
    <Flex flex={1} gap="xxs">
      <Typography variant="bodySemiboldM" textAlign="right">
        {`${rank}.`}
      </Typography>
      <Flex flex={1} gap="xxs" flexDirection="column">
        <Flex justifyContent="space-between" gap="s">
          <Typography variant="bodySemiboldM">{question}</Typography>
          {Boolean(possiblyNotApplicable) && (
            <Flex gap="xxxs" alignItems="center">
              <Typography variant="bodyRegularS">
                {t("not_applicable")}
              </Typography>
              {renderNotApplicableToggle ?? (
                <Toggle
                  checked={notApplicable}
                  onChange={onChangeNotApplicable}
                  disabled={loading}
                />
              )}
            </Flex>
          )}
        </Flex>
        {renderInputChoices ?? (
          <PickInputChoices
            mode={mode}
            value={rating}
            onChange={onChangeRating}
            ratingScale={ratingScale}
            maximumPoints={maximumPoints}
            disabled={loading || notApplicable}
          />
        )}
        <Flex justifyContent="space-between">
          <Flex flexDirection="column" gap="xxxs">
            {Boolean(notApplicable) && (
              <Typography variant="bodyMediumS">
                {t("not_applicable_full")}
              </Typography>
            )}
            {Boolean(description) && (
              <Typography variant="supportingRegularS">
                {description}
              </Typography>
            )}
          </Flex>
          <TotalPoints
            rating={rating}
            ratingScale={ratingScale}
            maximumPoints={maximumPoints}
            notApplicable={notApplicable}
          />
        </Flex>
        {renderFeedback ?? (
          <EvaluationTextarea
            comment={comment}
            loading={loading}
            hasAiAnswer={hasAiAnswer}
            onChangeComment={onChangeComment}
          />
        )}
      </Flex>
    </Flex>
  );
}
